import type { FC, FormEvent } from "react";
import { useState, useEffect, Fragment } from "react";
import {
    Box,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Stack,
    Autocomplete,
    Typography,
    InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { MenuItemDto } from "src/api/client-api/menu/useMenuAdmin";
import { MenuIconSelector } from "./IconSelector";
import { FoodsSelect, FoodsSelectServerPagination } from "@foodpilot/foods";
import { MenuItemFormSubmit } from "./Form.types";
import { translatedMenuLabel } from "./translatedMenuLabel";
import { useGetAllPermissions } from "src/api/client-api/permissions/useGetAllPermissions";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

const AllMenuItemType = ["item", "separator"] as const;
type MenuItemType = (typeof AllMenuItemType)[number];
type MenuOptionsType = {
    id: MenuItemType;
    label: string;
};

type MenuItemFormData = {
    type: MenuItemType;
    label: string;
    weight: number; // Used internally; hidden in UI. Backend will determine via DnD/processor when 0
    url: string | undefined;
    icon: string | undefined;
    moduleFeatureKey: string;
    isActive: boolean;
    parentId?: number | null;
    permissionId?: number;
};

type MenuItemFormProps = {
    initialData?: MenuItemDto | null;
    menuItems: MenuItemDto[];
    onSubmit: (data: MenuItemFormSubmit) => void;
    onCancel: () => void;
    isLoading?: boolean;
};

// Common icons used in FoodPilot
const ICON_OPTIONS = [
    "home",
    "company",
    "products",
    "ingredients",
    "packaging",
    "settings",
    "dashboard",
    "charts",
    "documentation",
    "users",
    "security",
    "reports",
    "calendar",
    "notifications",
    "help",
    "download",
    "upload",
    "search",
    "filter",
    "sort",
    "menu",
    "list",
    "grid",
    "table",
    "add",
    "edit",
    "delete",
    "save",
    "cancel",
    "check",
    "close",
    "arrow-left",
    "arrow-right",
];

// Common feature keys
const FEATURE_KEY_OPTIONS = [
    "FS-company-module",
    "FS-products-module",
    "FS-ingredients-module",
    "FS-packaging-module",
    "FS-strategy",
    "FS-cms-enabled",
    "FS-documentary-space",
];

export const MenuItemForm: FC<MenuItemFormProps> = ({
    initialData,
    menuItems,
    onSubmit,
    onCancel,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const isFeatureLinked = Boolean(initialData?.moduleFeatureKey);

    const [formData, setFormData] = useState<MenuItemFormData>({
        type: "item",
        label: "",
        weight: 0,
        url: "",
        icon: "",
        moduleFeatureKey: "",
        isActive: true,
        parentId: undefined,
        permissionId: undefined,
    });

    const [errors, setErrors] = useState<Partial<Record<keyof MenuItemFormData, string>>>({});

    // Initialize form with existing data
    useEffect(() => {
        if (initialData) {
            const type: MenuItemType = initialData.label === "separator" ? "separator" : "item";
            setFormData({
                type: type,
                label: initialData.label,
                weight: initialData.weight ?? 0,
                url: initialData.url || "",
                icon: initialData.icon || "",
                moduleFeatureKey: initialData.moduleFeatureKey || "",
                isActive: initialData.isActive,
                parentId: initialData.parent?.id,
                permissionId: initialData.permission?.id,
            });
        } else {
            // New items default to 0 so backend/drag&drop determines final order
            setFormData((prev) => ({ ...prev, weight: 0 }));
        }
    }, [initialData, menuItems]);

    const itemsById = new Map(menuItems.map((item) => [item.id, item]));
    const isInSubtreeOf = (item: MenuItemDto, rootId: number): boolean => {
        for (let current: MenuItemDto | undefined = item; current; current = itemsById.get(current.parent?.id ?? -1)) {
            if (current.id === rootId) return true;
        }
        return false;
    };
    const parentOptions = initialData ? menuItems.filter((item) => !isInSubtreeOf(item, initialData.id)) : menuItems;

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof MenuItemFormData, string>> = {};

        if (formData.type === "separator") return true;

        if (!formData.label.trim()) {
            newErrors.label = t("menu.form.error.label.required", "Label is required");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const { moduleFeatureKey: _ignored, ...rest } = formData;

        if (rest.type === "separator") {
            const separatorSubmit: MenuItemFormSubmit = {
                ...rest,
                label: SEPARATOR_ITEM.id,
            };
            onSubmit(separatorSubmit);
            return;
        }
        const submitData: MenuItemFormSubmit = {
            ...rest,
            url: formData?.url?.trim() || undefined,
            icon: formData?.icon?.trim() || undefined,
            permissionId: formData.permissionId,
        };

        onSubmit(submitData);
    };

    const handleFieldChange = (field: keyof MenuItemFormData, value: string | number | boolean | null | undefined) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const DEFAULT_ITEM: MenuOptionsType = { id: "item", label: t("menu_management.default_button", "Default button") };
    const SEPARATOR_ITEM: MenuOptionsType = { id: "separator", label: t("menu_management.separator", "Separator") };
    const MENU_TYPE_OPTIONS: MenuOptionsType[] = [DEFAULT_ITEM, SEPARATOR_ITEM];
    const selectedType = MENU_TYPE_OPTIONS.find((i) => i.id === formData.type) ?? DEFAULT_ITEM;

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
            <Stack spacing={3}>
                {!isFeatureLinked && (
                    <FoodsSelect
                        disableSearch
                        selectedOption={selectedType}
                        options={MENU_TYPE_OPTIONS}
                        onChange={(selectedValue) => {
                            if (selectedValue === null) {
                                return;
                            }
                            const newFormData = {
                                ...formData,
                                type: selectedValue.id,
                            };
                            setFormData(newFormData);
                        }}
                        getId={(item) => {
                            return item.id;
                        }}
                        getName={(item) => {
                            return item.label;
                        }}
                        textOptions={{
                            placeholder: "",
                            searchPlaceholder: "",
                            emptyOptionsText: "",
                            defaultValueText: undefined,
                            noResult: "",
                        }}
                    />
                )}

                {formData.type === "separator" ?
                    <SeparatorButtonFields />
                :   <DefaultButtonFields
                        formData={formData}
                        errors={errors}
                        handleFieldChange={handleFieldChange}
                        isFeatureLinked={isFeatureLinked}
                        featureLabel={initialData ? translatedMenuLabel(t, initialData) : undefined}
                        parentOptions={parentOptions}
                    />
                }

                {!isFeatureLinked && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => handleFieldChange("isActive", e.target.checked)}
                            />
                        }
                        label={t("menu.form.active", "Active")}
                    />
                )}

                <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        sx={(theme) => ({
                            minWidth: 120,
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            },
                            "&:disabled": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                opacity: 0.36,
                                color: "white !important",
                            },
                        })}
                    >
                        {isLoading ?
                            t("saving", "Saving...")
                        : initialData ?
                            t("update", "Update")
                        :   t("create", "Create")}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={isLoading}
                        sx={(theme) => ({
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            border: `1px solid ${theme.palette.primary[600]}`,
                            color: `${theme.palette.primary[600]} !important`,
                            backgroundColor: "transparent",
                            "&:hover": {
                                border: `1px solid ${theme.palette.primary[700]}`,
                                color: `${theme.palette.primary[700]} !important`,
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                            "&:disabled": {
                                border: `1px solid ${theme.palette.primary[600]}`,
                                color: `${theme.palette.primary[600]} !important`,
                                opacity: 0.36,
                            },
                        })}
                    >
                        {t("cancel", "Cancel")}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

const SeparatorButtonFields = () => {
    // For this one, for the moment, no special Fields :)
    return <></>;
};

type DefaultButtonProps = {
    formData: MenuItemFormData;
    handleFieldChange: (field: keyof MenuItemFormData, value: string | number | boolean | null | undefined) => void;
    errors: Partial<Record<keyof MenuItemFormData, string>>;
    isFeatureLinked: boolean;
    featureLabel?: string;
    parentOptions: MenuItemDto[];
};
const DefaultButtonFields = (props: DefaultButtonProps) => {
    const { handleFieldChange, formData, errors, isFeatureLinked, featureLabel, parentOptions } = props;
    const { t } = useTranslation();
    const { search, setSearch } = useDelayedSearch();

    const { permissions = [], isLoading: isLoadingPermissions } = useGetAllPermissions({
        search,
    });

    const selectedPermission = permissions.find((p) => p.id === formData.permissionId) || null;

    const isSearchFound = (searchValue: string) => {
        if (!search) return true;
        return searchValue.toLowerCase().includes(search.toLowerCase());
    };

    return (
        <Fragment>
            <TextField
                label={t("menu.form.label", "Label")}
                value={isFeatureLinked ? featureLabel : formData.label}
                onChange={(e) => handleFieldChange("label", e.target.value)}
                error={!!errors.label}
                helperText={
                    errors.label || (isFeatureLinked ? t("menu.form.label.locked", "Managed by feature") : undefined)
                }
                required
                fullWidth
                disabled={isFeatureLinked}
            />

            <Autocomplete
                options={parentOptions}
                getOptionLabel={(option) => translatedMenuLabel(t, option)}
                value={parentOptions.find((item) => item.id === formData.parentId) || null}
                onChange={(_, value) => handleFieldChange("parentId", value?.id ?? null)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("menu.form.parent", "Parent Item")}
                        helperText={t("menu.form.parent.help", "Leave empty for top-level item")}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props}>
                        <Box>
                            <Typography>{translatedMenuLabel(t, option)}</Typography>
                            {option.url && (
                                <Typography variant="caption" color="text.secondary">
                                    {option.url}
                                </Typography>
                            )}
                        </Box>
                    </li>
                )}
            />

            {!isFeatureLinked && (
                <Fragment>
                    <TextField
                        label={t("menu.form.url", "URL")}
                        value={formData.url}
                        onChange={(e) => handleFieldChange("url", e.target.value)}
                        helperText={t("menu.form.url.help", "Leave empty for drawer-style menu")}
                        InputProps={{
                            startAdornment: formData.url && !formData.url.startsWith("http") && (
                                <InputAdornment position="start">/</InputAdornment>
                            ),
                        }}
                        fullWidth
                    />

                    <MenuIconSelector
                        value={formData.icon}
                        onChange={(icon) => handleFieldChange("icon", icon)}
                        label={t("menu.form.icon", "Icon")}
                    />

                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {t("menu.form.permission", "Permission")}
                        </Typography>
                        <FoodsSelectServerPagination
                            selectedOption={selectedPermission}
                            options={isLoadingPermissions ? [] : permissions}
                            onChange={(permission) =>
                                handleFieldChange("permissionId", permission ? permission.id : null)
                            }
                            getId={(permission) => permission.id}
                            getName={(permission) => permission.identifier}
                            textOptions={{
                                placeholder: t("menu.form.permission.placeholder", "Select a permission"),
                                searchPlaceholder: t("menu.form.permission.search_placeholder", "Search permissions"),
                                emptyOptionsText: t("menu.form.permission.no_options", "No permissions available"),
                                defaultValueText: undefined,
                                noResult:
                                    isLoadingPermissions ?
                                        t("menu.form.permission.loading", "Loading")
                                    :   t("menu.form.permission.no_result", "No permissions found"),
                            }}
                            search={search}
                            setSearch={setSearch}
                            isSearchFound={isSearchFound}
                            noValue={t("menu.form.permission.no_permission", "No permission")}
                        />
                    </Box>
                </Fragment>
            )}
        </Fragment>
    );
};
